import { paintIconAction } from './82-icon-actions.mjs';
import { gameColourPresentation } from './35-game-board.mjs';
import { patternStampFootprint } from './51-creative-artwork.mjs';
import { createPictureStudio } from './64-picture-studio.mjs';

/** Compose the native tools disclosure beside the shared More menu. Their
 * coordinated lifecycle keeps focus and disclosure listeners card-owned. */
export function createGameTools(ctx, game, { more, moreActions, host, canOpen, onOpen }) {
  const tools = ctx.e('details', 'game-tools');
  const toolsSummary = ctx.e('summary', '', game.id === 'thread-loom' ? 'Threads' : 'Tools');
  const toolActions = ctx.e('div', 'game-tool-actions');
  tools.append(toolsSummary, toolActions);
  const detachDisclosures = attachGameDisclosures(tools, more);
  const pictureStudio =
    game.id === 'tile-slide' ? createPictureStudio(ctx, { host, game, canOpen, onOpen }) : null;
  if (pictureStudio) moreActions.append(pictureStudio.button);
  return { tools, toolsSummary, toolActions, detachDisclosures, pictureStudio };
}

const mergeDirections = [
  ['arrow-left', 'Move tiles left'],
  ['arrow-right', 'Move tiles right'],
  ['arrow-up', 'Move tiles up'],
  ['arrow-down', 'Move tiles down'],
];

function stampPreview(ctx, configuration, stamp) {
  const preview = ctx.e('span', 'game-stamp-preview');
  preview.setAttribute('aria-hidden', 'true');
  preview.style.setProperty('--stamp-ink', `var(--game-colour-${configuration.ink})`);
  for (const index of patternStampFootprint(configuration, stamp)) {
    const dot = ctx.e('i');
    dot.style.gridColumn = String((index % 6) + 1);
    dot.style.gridRow = String(Math.floor(index / 6) + 1);
    preview.append(dot);
  }
  return preview;
}

/** Keep palette, tray and tool controls keyed and keyboard-accessible across moves.
 * The completion controller owns the sole Next control for a held stage. */
export function paintGameOptions(
  ctx,
  { view, game, options, tools, toolActions, optionNodes, move, action, disabled, readView },
) {
  const advanced = [];
  const desired = view.options
    .map((item) => {
      if (view.advanceAction && item.move.index === view.advanceAction.index) return null;
      const key = String(item.move.index);
      let button = optionNodes.get(key);
      if (!button) {
        button = action(item.label, item.label, () => {
          const current = readView().options.find((value) => String(value.move.index) === key);
          if (current && !current.disabled) {
            if (game.id === 'block-fit' && current.move.index === 0)
              options.dataset.rotatingPiece = String(
                readView().options.find((value) => value.piece && value.selected)?.move.index ?? '',
              );
            move(current.move);
          }
        });
        optionNodes.set(key, button);
      }
      button.className = 'game-action';
      const oldStampPreview = button.querySelector?.('.game-stamp-preview');
      const rotating = game.id === 'block-fit' && item.move.index === 0;
      const direction = game.id === 'soft-merge' ? mergeDirections[item.move.index] : null;
      if (!rotating && !direction) {
        button.textContent = item.label;
        button.setAttribute('aria-label', item.label);
      }
      button.setAttribute('aria-pressed', String(Boolean(item.selected)));
      button.dataset.option = key;
      button.dataset.selected = String(Boolean(item.selected));
      if (direction) paintIconAction(ctx, button, direction[0], direction[1]);
      if (rotating) {
        button.classList.add('game-rotate-piece');
        paintIconAction(ctx, button, 'rotate-cw', 'Rotate selected piece clockwise');
      }
      if (Number.isInteger(item.colour)) {
        const swatch = gameColourPresentation(item.colour);
        button.classList.add('game-swatch');
        button.textContent = swatch.shape;
        button.style.setProperty('--swatch-colour', swatch.colour);
        button.title = item.label;
      }
      if (item.piece) {
        const preview = ctx.e('span', 'game-piece-preview');
        preview.setAttribute('aria-hidden', 'true');
        if (game.id === 'block-fit' && options.dataset.rotatingPiece === key)
          preview.classList.add('game-piece-turn');
        for (const [x, y] of item.piece) {
          const square = ctx.e('i');
          square.style.gridColumn = String(x + 1);
          square.style.gridRow = String(y + 1);
          preview.append(square);
        }
        button.replaceChildren(preview);
      }
      if (game.id === 'pattern-stamp' && item.move.index >= 6 && item.move.index <= 9) {
        const signature = `${item.move.index}:${view.scene.rotation}:${view.scene.symmetry}:${view.scene.ink}`;
        const preview =
          button.dataset.stampPreview === signature && oldStampPreview
            ? oldStampPreview
            : stampPreview(ctx, view.scene, item.move.index - 6);
        button.dataset.stampPreview = signature;
        button.replaceChildren(preview, ctx.e('span', '', item.label));
      }
      button.disabled = disabled || Boolean(item.disabled);
      if (
        game.kind === 'idle' &&
        view.options.some((value) => value.tool === 'palette') &&
        item.tool !== 'palette' &&
        !item.completionAction
      ) {
        advanced.push(button);
        return null;
      }
      return button;
    })
    .filter(Boolean);
  if (advanced.length) {
    if (game.id === 'pattern-stamp') {
      const summary = tools.querySelector?.('summary');
      const signature = `${view.scene.stamp}:${view.scene.rotation}:${view.scene.symmetry}:${view.scene.ink}`;
      if (summary && summary.dataset.stampPreview !== signature) {
        const chosen = view.options.find((item) => item.move.index === view.scene.stamp + 6);
        summary.dataset.stampPreview = signature;
        const selectedPreview = optionNodes
          .get(String(view.scene.stamp + 6))
          ?.querySelector?.('.game-stamp-preview');
        summary.replaceChildren(
          selectedPreview?.cloneNode?.(true) || stampPreview(ctx, view.scene, view.scene.stamp),
          ctx.e('span', '', chosen.label),
        );
        summary.setAttribute(
          'aria-label',
          `${chosen.label} stamp · ${view.scene.rotation * 90} degrees${view.scene.symmetry ? ' · mirrored' : ''} · open tools`,
        );
      }
    }
    const kept = new Set(advanced);
    for (const node of [...toolActions.children]) if (!kept.has(node)) node.remove();
    let cursor = toolActions.firstChild;
    for (const node of advanced) {
      if (node !== cursor) toolActions.insertBefore(node, cursor);
      cursor = node.nextSibling;
    }
    desired.push(tools);
  }
  delete options.dataset.rotatingPiece;
  const retained = new Set(desired);
  for (const node of [...options.children]) if (!retained.has(node)) node.remove();
  let next = options.firstChild;
  for (const node of desired) {
    if (node !== next) options.insertBefore(node, next);
    next = node.nextSibling;
  }
}

/** Group one card's tools and options without changing another card. Native
 * named details close the sibling before layout; toggle preserves the same
 * exclusivity where grouping is unsupported. The owner releases both listeners. */
export function attachGameDisclosures(tools, more) {
  const disclosureName = `game-controls-${crypto.randomUUID()}`;
  tools.setAttribute('name', disclosureName);
  more.setAttribute('name', disclosureName);
  const toolsToggle = () => {
    if (tools.open) more.open = false;
  };
  const moreToggle = () => {
    if (more.open) tools.open = false;
  };
  tools.addEventListener('toggle', toolsToggle);
  more.addEventListener('toggle', moreToggle);
  return () => {
    tools.removeEventListener('toggle', toolsToggle);
    more.removeEventListener('toggle', moreToggle);
  };
}

/** Present already-authorized scoreboard data; request and actor lifecycle stay
 * with the card controller. Text nodes keep public display names inert. */
export function showPublishedGameScores(ctx, game, result) {
  const content = ctx.e('div', 'game-scores');
  content.append(ctx.e('h3', '', game.title + ' · Published scores'));
  if (!result.entries.length)
    content.append(ctx.e('p', '', 'No published scores yet. Private rounds never appear here.'));
  const list = ctx.e('ol');
  for (const entry of result.entries)
    list.append(ctx.e('li', '', `${entry.label} · ${entry.score} points`));
  content.append(list, ctx.e('p', '', 'Choose private, anonymous or named scores in Settings.'));
  ctx.dialog('Published scores', content);
}
